<template>
	<el-row>
		<el-col :span="18">
			<div class="base-form header">				
				<h2>Группы сервисных компонетов (узлов)</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddComponentGroupClick">
					Новая группа сервисных компонентов (узлов)
				</el-button>
			</div>
		</el-col>
	</el-row>
	
	<div class="base-form filter">			
		<el-row>
			<el-col :span="24">
				<el-input v-model="tableSearchQuery" style="max-width: 600px" clearable placeholder="Поиск по названию группы компонетов (узлов)"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</el-col>
		</el-row>
	</div>

	<el-row>
		<el-col :span="24">
			<el-table
				row-key="id" 				
				v-loading="tableLoading"	
				:data="tableData"			
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
				@sort-change="onTableSortingChange"
				@row-click="onTableRowClick">				
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onPickJobComponentGroupClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Группа" prop="name" sortable column-key="name" />
				<el-table-column label="Описание" prop="description" column-key="description" />		
				<el-table-column v-if="!isPickerMode" align="right" width="74">
					<template #default="scope">		
						<el-button 
							:icon="deleteIcon" 
							type="danger"
							circle
							@click="(e) => onDeleteJobComponentGroupClick(scope.row.id, scope.row.name, e)"
						/>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
	<el-row v-if="isPickerMode">
		<el-col>
			<div class="base-form">
				<el-button @click="onCloseCurrentTabClick">
					Отмена
				</el-button>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search, Delete } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import ApplicationTab from '@/models/ApplicationTab';
import TabsDictionary from '@/variables/tabs';
import store from '@/store';
import { noun } from '@/helpers/formatters';
import EventHelper from '@/helpers/eventsHelper';
import { WatchStopHandle, watch } from 'vue';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import ServiceJobComponentModel from '@/models/ServiceJobComponentModel';
import ServiceJobComponentGroupModel from '@/models/ServiceJobComponentGroupModel';
import JobComponentGroupsApiHelper from '@/helpers/api/JobComponentGroupsApiHelper';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	},
	props: {
		params: Array
	}
})
export default class ServiceJobComponentGroupsView extends Vue {
	/** params */
	params!: Array<any>;

	/** constants */	
	deleteIcon = Delete;
	refreshEvent = 'jobComponentGroupsUpdate';
	isPickerMode = false;
	onPickedCallback: Function | null = null;
	circleIcon = CirclePlus;
	searchIcon = Search;
	componentGroupsApiHelper = new JobComponentGroupsApiHelper();
	
	/** view-models */
	tableLoading = false;
	tableData: Array<ServiceJobComponentGroupModel> = [];
	tableSearchQuery = '';
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'name';
	tableSortOrder = 'ascending';
	
	watchTableSearchQuery: WatchStopHandle | null = null;

	mounted() {
		this.initView();
		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchJobComponentsGroupsAsync()
		);

		EventHelper.setEventListener(this.refreshEvent, this.fetchJobComponentsGroupsAsync);
		EventHelper.dispatchEvent(this.refreshEvent);
	}

	unmounted() {
		if (this.watchTableSearchQuery) {
			this.watchTableSearchQuery();
		}

		EventHelper.removeEventListener(this.refreshEvent, this.fetchJobComponentsGroupsAsync);
	}

	initView() {
		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		/** check if picker mode activated */
		if (pickerModeArgument && 
			pickerModeArgument.isPickerModeEnabled === true &&
			pickerModeArgument.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback;
		}
	}

	onTableRowClick(row: ServiceJobOperationModel) {
		if (this.isPickerMode) {
			return;
		}
		const viewTab = TabsDictionary.jobComponentGroupView;		
		viewTab.params = [ row, false ];
		store.commit('addTab', viewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onAddComponentGroupClick() {
		const addTab = TabsDictionary.jobComponentGroupAdd;
		const addNewComponent = true;
		addTab.params = [ null, addNewComponent ];
		store.commit('addTab', addTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	async fetchJobComponentsGroupsAsync() {		
		try {
			this.tableLoading = true;			
			const filterObject = {
				orderBy: this.tableSortColumn ? this.tableSortColumn : 'name',
				orderAsc: this.tableSortOrder == 'ascending' ? true : false,
				query: this.tableSearchQuery
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/servicejobcomponentgroups', { params: filterObject });
			const groupsTree = this.mapGroupsTree(response.data);
			this.tableData = groupsTree;
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: `Ошибка соединения, не удалось загрузить группы сервисных компонетов (узлов)`,
					type: 'error',
				});
			} else {
				ElNotification({
					message: `Ошибка загрузка групп сервисных компонетов (узлов)`,
					type: 'error',
				});
			}
			console.error('Error loading service-job-component-groups list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async deleteJobComponentGroupAsync(componentGroupId: number) {
		try {
			this.tableLoading = true;
			const isDeleted = await this.componentGroupsApiHelper.deleteJobComponentGroupAsync(store.getters.jwt, componentGroupId);
			if (!isDeleted) {
				throw new Error('Unable to delete component group');
			}
			ElNotification({
				title: 'Группа сервисных компонентов удалена',
				type: 'success',
			});	
			EventHelper.dispatchEvent('jobComponentGroupsUpdate');			
		} catch (e) {			
			ElNotification({
				title: 'Ошибка, группа сервисных компонентов не была удалена',
				type: 'error',
			});				
		} finally {
			this.tableLoading = false;
		}		
	}

	async onDeleteJobComponentGroupClick(groupId: number, groupName: string, e: Event) {
		e.stopPropagation();
		const relatedJobIdsToGroup = await this.componentGroupsApiHelper.getRelatedJobIdsAsync(store.getters.jwt, groupId);

		let confirmationMessage: string;

		if (!relatedJobIdsToGroup ||
			relatedJobIdsToGroup?.length === 0) {
			confirmationMessage = `Группа сервисных компонентов '${groupName}' будет удалена`;
		} else {
			const conjunctedComponentNoun = noun.conjunct(relatedJobIdsToGroup!.length, 'компонет', 'компонетов', 'компонета');
			confirmationMessage = `В группе '${groupName}' есть ${relatedJobIdsToGroup?.length} ${conjunctedComponentNoun}. При удалении группы все компоненты будут перемещены в группу по-умолчанию`
		}

		ElMessageBox.confirm(
			confirmationMessage,
			{
				confirmButtonText: 'OK',
				cancelButtonText: 'Отмена',
				type: 'error',
			}
		)
		.then(() => this.deleteJobComponentGroupAsync(groupId));		
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder == a.order;
		this.fetchJobComponentsGroupsAsync();
	}

	/** handles callback and passes selected component-group if picker-mode enabled */	
	onPickJobComponentGroupClick(component: ServiceJobComponentModel, e: Event) {
		e.stopPropagation();
		if (!this.onPickedCallback) {
			return;
		}
		this.onPickedCallback(component);
	}

	onCloseCurrentTabClick() {		
		const currentTabId = store.getters.tab;
		store.commit('removeTab', currentTabId);
	}

	mapGroupsTree(data: Array<any>) : Array<ServiceJobComponentGroupModel> {
		const map = this.mapGroupsTree;
		return data.map((g: any) => {

			const group = new ServiceJobComponentGroupModel(g);
			if (!g?.childGroups?.length) {
				group.children = null;
			} 
			else {
				group.children = map(g.childGroups);
			}
			
			return group;
		});
	}
}
</script>